import { cl, getCookie, setCookie } from '@/utils';
import { classMap } from 'lit-html/directives/class-map.js';
import { html, LitElement } from 'lit';
import { property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { bus } from '@/bus';
import type { StorefrontApiClient } from '@shopify/storefront-api-client';
import { getCartQuery } from '@/shopify/queries/cart.storefront';
import { createCartItemMutation, createCartMutation, deleteCartItemsMutation } from '@/shopify/mutations/cart.storefront';
import { normalizeCart } from '@/shopify/normalize';
import { PlatformCart, PlatformItemInput } from '@/shopify/types';
import { CreateCartItemMutation, CreateCartMutation, DeleteCartItemsMutation, SingleCartQuery } from '@/shopify/types/storefront.generated';
import ShopifyCartToggle from '@/components/ShopifyCartToggle';

export default class ShopifyCart extends LitElement {
  static component = 'b-shopify-cart';
  static CART_COOKIE = 'shopify-cart-id';
  client: StorefrontApiClient;
  cartToggleEl: ShopifyCartToggle | null = null;

  @property()
  cart: PlatformCart | null = null;

  @property()
  isOpen = false;
  disabled = import.meta.env.VITE_SHOPIFY_DISABLED === 'true';

  @property()
  loading = false;

  protected createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();

    this.cartToggleEl = document.querySelector('b-shopify-cart-toggle');
    import('@shopify/storefront-api-client').then(async (module) => {
      if (!module) return;
      const { createStorefrontApiClient } = module;
      this.client = createStorefrontApiClient({
        apiVersion: '2024-07',
        storeDomain: import.meta.env.VITE_SHOPIFY_HOSTNAME,
        publicAccessToken: import.meta.env.VITE_SHOPIFY_STOREFRONT,
      });

      if (!this.disabled) {
        await this.getCart();
      }
    });

    bus.on('menuToggle', () => {
      this.isOpen = false;
    })
  }

  setCart(cart: SingleCartQuery["cart"]) {
    this.cart = normalizeCart(cart);
    bus.emit('cart', this.cart);
    this.cartToggleEl?.setQty(this.cart?.totalQuantity || 0);
    return this.cart;
  }

  async createCart(items: PlatformItemInput[]): Promise<PlatformCart | undefined | null> {
    const cart = await this.client.request<CreateCartMutation>(createCartMutation, {
      variables: { items },
    });
    return this.setCart(cart.data?.cartCreate?.cart);
  }

  async fetchCart(cartId: string): Promise<PlatformCart | undefined | null> {
    const cart = await this.client.request<SingleCartQuery>(getCartQuery, {
      variables: { cartId },
    });
    return this.setCart(cart.data?.cart);
  }

  async createCartItem(items: PlatformItemInput[]) {
    if (this.cart) {
      const cart = await this.client.request<CreateCartItemMutation>(createCartItemMutation, {
        variables: { cartId: this.cart.id, items },
      });
      this.setCart(cart.data?.cartLinesAdd?.cart);
    }
  }

  // async updateCartItem(items: PlatformItemInput[]) {
  //   const cart = await this.client.request<UpdateCartItemsMutation>(updateCartItemsMutation, {
  //     variables: { cartId: this.cart.id, items },
  //   });
  //   this.cart = normalizeCart(cart.data?.cartLinesUpdate?.cart);
  //
  //   bus.emit('cart', this.cart);
  // }

  async deleteCartItem(itemIds: string[]) {
    if (this.cart) {
      const cart = await this.client.request<DeleteCartItemsMutation>(deleteCartItemsMutation, {
        variables: { cartId: this.cart.id, itemIds },
      });
      this.setCart(cart.data?.cartLinesRemove?.cart);
    }
  }

  async getCart() {
    const cartId = getCookie(ShopifyCart.CART_COOKIE);
    if (cartId) {
      await this.fetchCart(cartId);
    } else {
      await this.createCart([]);
      if (this.cart) {
        setCookie(ShopifyCart.CART_COOKIE, this.cart.id, 30);
      }
    }
  }

  toggleOpen() {
    bus.emit('cartToggle');
    this.isOpen = !this.isOpen;
  }

  addToCart(variantId: string) {
    return this.createCartItem([{ merchandiseId: variantId, quantity: 1 }])
  }

  removeFromCart(lineId: string, ev: MouseEvent) {
    this.loading = true;
    cl((ev.target as HTMLElement).closest<HTMLElement>('.cart-item')).add('is-removing');
    this.deleteCartItem([lineId]).then(() => {
      this.loading = false;
      if (!this.cart?.totalQuantity) {
        this.toggleOpen();
      }
    })
  }

  render() {
    return this.cart && !this.disabled
      ? html`<div class=${classMap({ 'is-open': this.isOpen, 'cart group/cart transition-transform duration-500 -translate-y-full [&.is-open]:translate-y-0 [&.is-open]:pointer-events-auto': true, 'is-empty': !this.cart?.totalQuantity })}>
        <div class="p-section flex flex-col bg-bg-alt">
          <div class="pb-section text-20-c font-bold uppercase flex items-center justify-between">
            <div>Your Cart</div>
            <button class="hover-text block w-7 lg:w-9" @click="${this.toggleOpen}">
              <svg viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"><path d="M10.472 31.4721L8.52783 29.5279L18.0553 20L8.52783 10.4721L10.472 8.52792L19.9999 18.0554L29.5278 8.52792L31.472 10.4721L21.9445 20L31.472 29.5279L29.5278 31.4721L19.9999 21.9446L10.472 31.4721Z" /></svg>
            </button>
          </div>
          <div class="flex-1 mb-4">
            ${repeat(this.cart.items, (item) => item.id, (item) => {
              return html`<b-height class="block">
                <div class="cart-item">
                  <div class="pb-4">
                    <div class="flex justify-between pb-1 text-20-c font-bold uppercase">
                      <div>
                        ${item.merchandise.product.title}
                        ${item.merchandise.title !== 'Default Title' ? ' – ' + item.merchandise.title : ''}
                      </div>
                      <div>$${item.cost.totalAmount.amount}</div>
                    </div>
                    <div class="flex text-12 uppercase font-medium">
                      <div class="pr-2">Qty</div>
                      <div class="pr-4">${item.quantity}</div>
                      <div class="hover-text" @click="${(ev: MouseEvent) => this.removeFromCart(item.id, ev)}">Remove</div>
                    </div>
                  </div>
                </div>
              </b-height>`
            })}
          </div>
          <div class="flex justify-between text-20-c font-bold uppercase pt-section-2x pb-section">
            <div>Subtotal</div>
            <div>$${this.cart?.cost.totalAmount.amount}</div>
          </div>
          <a href="${this.cart?.checkoutUrl || '#'}" class=${classMap({ 'is-loading': this.loading, 'button is-white': true })}>Checkout</a>
        </div>
    </div>` : '';
  }
}
